import React from 'react'
import { CategorySelector } from 'submission/components/deal-submission/category-selector'
import { CountrySelector } from 'submission/components/deal-submission/country-selector'
import { FileUpload } from 'submission/components/deal-submission/file-upload'
import useFileUploader from 'hooks/useFileUploader'
import {
  type EquityFormData,
  DEAL_TYPE_OPTIONS,
} from 'submission/lib/equity-form-defaults'

type Props = {
  form: EquityFormData
  setForm: React.Dispatch<React.SetStateAction<EquityFormData>>
  errors: Record<string, string>
}

const OverviewSection: React.FC<Props> = ({ form, setForm, errors }) => {
  const { uploadFile } = useFileUploader()

  const onUploadLogo = (file: File | null) => {
    if (!file) {
      setForm((prev) => ({ ...prev, logoUrl: '' }))
      return
    }
    uploadFile(
      { file, path: `submission/logo-${Date.now()}-${file.name}` },
      (res) =>
        setForm((prev) => ({
          ...prev,
          logoUrl: res?.url ?? res ?? '',
        })),
    )
  }

  const onUploadPitch = (file: File | null) => {
    if (!file) {
      setForm((prev) => ({ ...prev, pitchDeckUrl: '' }))
      return
    }
    uploadFile(
      { file, path: `submission/pitchdeck-${Date.now()}-${file.name}` },
      (res) =>
        setForm((prev) => ({
          ...prev,
          pitchDeckUrl: res?.url ?? res ?? '',
        })),
    )
  }

  const logoFile = form.logoUrl
    ? (new File([], 'logo.png', { type: 'image/png' }) as File)
    : null
  const pitchFile = form.pitchDeckUrl
    ? (new File([], 'pitch-deck.pdf', { type: 'application/pdf' }) as File)
    : null

  return (
    <section className="bg-white border border-slate-200 rounded p-6">
      <h2 className="text-lg font-semibold text-slate-900 mb-4">
        Project overview
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="flex flex-col text-sm md:col-span-2">
          <span className="text-slate-700 mb-1">Project name *</span>
          <input
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.projectName}
            onChange={(e) =>
              setForm((p) => ({ ...p, projectName: e.target.value }))
            }
          />
          {errors.projectName && (
            <span className="text-red-600 text-xs mt-1">
              {errors.projectName}
            </span>
          )}
        </label>

        <label className="flex flex-col text-sm md:col-span-2">
          <span className="text-slate-700 mb-1">Project description *</span>
          <textarea
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            rows={3}
            value={form.projectDescription}
            onChange={(e) =>
              setForm((p) => ({ ...p, projectDescription: e.target.value }))
            }
          />
          {errors.projectDescription && (
            <span className="text-red-600 text-xs mt-1">
              {errors.projectDescription}
            </span>
          )}
        </label>

        <label className="flex flex-col text-sm md:col-span-2">
          <span className="text-slate-700 mb-1">Memo (optional)</span>
          <input
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.projectMemo}
            onChange={(e) =>
              setForm((p) => ({ ...p, projectMemo: e.target.value }))
            }
          />
        </label>

        <div className="md:col-span-2">
          <span className="text-sm text-slate-700 mb-1 block">Logo *</span>
          <FileUpload
            mode="image"
            accept="image/png"
            buttonLabel="Upload logo"
            helperText="PNG only, 512x512"
            maxSizeMB={5}
            errorStrings={{
              fileCount: 'Please upload one image.',
              fileType: 'PNG only',
              fileSize: 'Max 5 MB',
              unreadable: 'File appears corrupted',
              password: 'File appears protected',
              empty: 'File is empty',
              generic: 'Upload failed. Please try again.',
              linkDropNotAllowed: 'Cannot drop a link',
            }}
            ariaLabel="Upload project logo"
            onFileSelect={onUploadLogo}
            currentFile={logoFile}
            onRemove={() => setForm((p) => ({ ...p, logoUrl: '' }))}
            requiredWidth={512}
            requiredHeight={512}
          />
          {errors.logoUrl && (
            <span className="text-red-600 text-xs mt-1 block">
              {errors.logoUrl}
            </span>
          )}
        </div>

        <div className="md:col-span-2">
          <span className="text-sm text-slate-700 mb-1 block">
            Category tags *
          </span>
          <CategorySelector
            selectedCategories={form.categoryTags}
            onCategoriesChange={(categoryTags) =>
              setForm((p) => ({ ...p, categoryTags }))
            }
            maxCategories={5}
          />
          {errors.categoryTags && (
            <span className="text-red-600 text-xs mt-1 block">
              {errors.categoryTags}
            </span>
          )}
        </div>

        <label className="flex flex-col text-sm">
          <span className="text-slate-700 mb-1">Website *</span>
          <input
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.websiteUrl}
            onChange={(e) =>
              setForm((p) => ({ ...p, websiteUrl: e.target.value }))
            }
          />
          {errors.websiteUrl && (
            <span className="text-red-600 text-xs mt-1">
              {errors.websiteUrl}
            </span>
          )}
        </label>
        <label className="flex flex-col text-sm">
          <span className="text-slate-700 mb-1">X (Twitter)</span>
          <input
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.xUrl}
            onChange={(e) => setForm((p) => ({ ...p, xUrl: e.target.value }))}
          />
        </label>
        <label className="flex flex-col text-sm">
          <span className="text-slate-700 mb-1">Discord</span>
          <input
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.discordUrl}
            onChange={(e) =>
              setForm((p) => ({ ...p, discordUrl: e.target.value }))
            }
          />
        </label>

        <div className="md:col-span-2">
          <span className="text-sm text-slate-700 mb-1 block">
            Pitch deck *
          </span>
          <FileUpload
            mode="document"
            accept="application/pdf"
            buttonLabel="Upload pitch deck"
            helperText="PDF, up to 25 MB"
            maxSizeMB={25}
            errorStrings={{
              fileCount: 'Please upload one document.',
              fileType: 'PDF only',
              fileSize: 'Max 25 MB',
              unreadable: 'File appears corrupted',
              password: 'File appears protected',
              empty: 'File is empty',
              generic: 'Upload failed. Please try again.',
              linkDropNotAllowed: 'Cannot drop a link',
            }}
            ariaLabel="Upload pitch deck"
            onFileSelect={onUploadPitch}
            currentFile={pitchFile}
            onRemove={() => setForm((p) => ({ ...p, pitchDeckUrl: '' }))}
          />
          {errors.pitchDeckUrl && (
            <span className="text-red-600 text-xs mt-1 block">
              {errors.pitchDeckUrl}
            </span>
          )}
        </div>

        <label className="flex flex-col text-sm md:col-span-2">
          <span className="text-slate-700 mb-1">
            Research report URL (optional)
          </span>
          <input
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.researchReportUrl}
            onChange={(e) =>
              setForm((p) => ({ ...p, researchReportUrl: e.target.value }))
            }
          />
        </label>

        <label className="flex items-center text-sm md:col-span-2 gap-2">
          <input
            type="checkbox"
            checked={form.requestResearchSupport}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                requestResearchSupport: e.target.checked,
              }))
            }
          />
          Request research support
        </label>

        <label className="flex flex-col text-sm">
          <span className="text-slate-700 mb-1">Deal type *</span>
          <select
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.dealType}
            onChange={(e) =>
              setForm((p) => ({ ...p, dealType: e.target.value }))
            }
          >
            <option value="">Select...</option>
            {DEAL_TYPE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          {errors.dealType && (
            <span className="text-red-600 text-xs mt-1">{errors.dealType}</span>
          )}
        </label>

        <div>
          <span className="text-sm text-slate-700 mb-1 block">
            Jurisdiction *
          </span>
          <CountrySelector
            selectedCountries={form.jurisdiction ? [form.jurisdiction] : []}
            onCountriesChange={(list) =>
              setForm((p) => ({ ...p, jurisdiction: list[0] ?? '' }))
            }
            maxCountries={1}
          />
          {errors.jurisdiction && (
            <span className="text-red-600 text-xs mt-1 block">
              {errors.jurisdiction}
            </span>
          )}
        </div>

        <label className="flex flex-col text-sm">
          <span className="text-slate-700 mb-1">
            Target raise (USD millions) *
          </span>
          <input
            type="number"
            min={0}
            step="any"
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.targetRaise}
            onChange={(e) =>
              setForm((p) => ({ ...p, targetRaise: e.target.value }))
            }
          />
          {errors.targetRaise && (
            <span className="text-red-600 text-xs mt-1">
              {errors.targetRaise}
            </span>
          )}
        </label>
        <label className="flex flex-col text-sm">
          <span className="text-slate-700 mb-1">FDV (USD millions) *</span>
          <input
            type="number"
            min={0}
            step="any"
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.fdv}
            onChange={(e) => setForm((p) => ({ ...p, fdv: e.target.value }))}
          />
          {errors.fdv && (
            <span className="text-red-600 text-xs mt-1">{errors.fdv}</span>
          )}
        </label>
      </div>
    </section>
  )
}

export default OverviewSection
